import { Formik } from 'formik';
import type { FormikHelpers } from 'formik';
import { postJson } from 'requests/http';
import { trackError } from 'helpers/track_error';
import { Alert, formatPhoneNumber } from '@gusto/workbench';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSignupContext } from 'packages/signup/components/signupProvider';
import sendDataWithoutFormatToPayload from 'packages/signup/components/signupForm/sendDataWithoutFormatToPayload';
import { removePasswordOfCompanyLeadObject } from 'packages/signup/helpers/validateSurvey';
import trackFormSubmitClickedWithPii from 'packages/signup/analytics/trackFormSubmitClickedWithPii';
import type {
  CompanyLead,
  FormDetailsValues,
  LeadOriginData,
} from 'packages/signup/components/signupForm/types';
import sanitizeAndCombineUrl from 'packages/signup/components/signupForm/sanitizeAndCombineUrl';
import validationSchema from './validation_schema';
import FormDetails from './formDetails';
import getSubmitPayload from './getSubmitPayload';
import trackSurveySignupAnswerSubmitted from '../../analytics/trackSurveySignupAnswerSubmitted';
import trackSurveySignupAnswerSubmittedAndFailed from '../../analytics/trackSurveySignupAnswerSubmittedAndFailed';
import trackSignupClickAfterValidation from '../../analytics/trackSignupClickAfterValidation';
import trackFormSubmissionForPixel from '../../analytics/trackFormSubmission';

type CompanySignupFormProps = {
  companyLead: CompanyLead;
  fromSSO: boolean;
  ssoProvider?: string;
};

export default function CompanySignupForm({
  companyLead,
  fromSSO,
  ssoProvider,
}: CompanySignupFormProps) {
  const {
    numberEmployeesSegmentation,
    prevPayrollProvider,
    industryIdentification,
    intentStep,
    leadSource,
    tierSource,
    tier,
    submissionOrigin,
  } = useSignupContext();
  const { search } = useLocation();
  const [searchParams] = useSearchParams();

  const primaryCTAOnClick = async (values: CompanyLead) => {
    let validationSuccess = null;
    try {
      await validationSchema(fromSSO, companyLead.email).validate(values, { abortEarly: false });
      validationSuccess = true;
    } catch (error: any) {
      validationSuccess = false;
      trackError({
        error: new Error('CompanySignupForm frontend validation on submit'),
        severity: 'warning',
        custom: {
          message: error?.message,
        },
      });
    }

    const companyLeadObj = values;
    companyLeadObj.lead_origin_subtype = leadSource;
    companyLeadObj.lead_origin = 'GCorp';
    companyLeadObj.submitted_from = submissionOrigin;
    const companyLeadObjectWithoutPassword = removePasswordOfCompanyLeadObject(companyLeadObj);
    trackFormSubmitClickedWithPii(companyLeadObjectWithoutPassword, validationSuccess);
  };

  const onSubmit = (values: FormDetailsValues, formikOpts: FormikHelpers<FormDetailsValues>) => {
    const dataWithoutFormat = sendDataWithoutFormatToPayload(values);

    trackSignupClickAfterValidation(dataWithoutFormat);

    const surveyAnswers = {
      numberEmployeesSegmentation,
      prevPayrollProvider,
      industryIdentification,
      intentStep,
    };

    const leadOriginData: LeadOriginData =
      searchParams.get('from_bd') === 'true'
        ? { leadOrigin: 'GCorp', leadOriginSubtype: 'Company Creation Form' }
        : {};

    const payload = getSubmitPayload(
      dataWithoutFormat,
      surveyAnswers,
      leadSource,
      tierSource,
      tier,
      companyLead,
      submissionOrigin || 'Direct',
      leadOriginData
    );
    postJson('/homepage-survey/company', payload)
      .then(response => {
        trackSurveySignupAnswerSubmitted(payload.company_lead, surveyAnswers);

        // If new account create form is created send this event on submit after validation
        trackFormSubmissionForPixel();
        window.location.href = sanitizeAndCombineUrl(response.redirect_url, search);
      })
      .catch(error => {
        const errorStatus = error?.cause?.status || '500';
        const errorMessage = error?.cause?.statusText || 'error from CompanySignupForm';

        formikOpts.setStatus({ unknownError: true, errorStatus });
        formikOpts.setSubmitting(false);
        trackSurveySignupAnswerSubmittedAndFailed(
          payload.company_lead,
          surveyAnswers,
          errorStatus,
          errorMessage
        );
        trackError({
          error: new Error(`[CompanySignupForm] Failed with status: ${errorStatus}`),
          custom: {
            message: errorMessage,
          },
        });
      });
  };

  return (
    <Formik
      initialValues={{
        firstName: companyLead?.first_name || '',
        lastName: companyLead?.last_name || '',
        email: companyLead?.email || '',
        companyName: companyLead?.company_name || '',
        numberEmployees: companyLead?.number_employees || '',
        phone: companyLead?.phone ? formatPhoneNumber(companyLead.phone) : '',
        password: '',
        passwordConfirmation: '',
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema(fromSSO, companyLead.email)}
    >
      {({ values, setStatus, status }) => {
        return (
          <>
            <FormDetails
              values={values}
              fromSSO={fromSSO}
              ssoProvider={ssoProvider}
              setStatus={setStatus}
              emailStatus={status?.email}
              errorStatus={status?.errorStatus}
              primaryCTAOnClick={() => primaryCTAOnClick(values)}
            />
            {status?.unknownError && (
              <Alert label="Error" severity="error">
                We&apos;re experiencing technical issues, Please try again later.
              </Alert>
            )}
          </>
        );
      }}
    </Formik>
  );
}
